import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Skeleton, Tooltip } from '@patternfly/react-core';
import InsightsActions from './InsightsActions';
import { Widget } from '../../types/dashboards/dashboard';
import { InsightsActionsResponse } from '../../api/types';
import {
	faSparkles,
	faTimes,
	faExpand,
	faCompass,
	faChevronUp,
	faChevronDown,
} from '@fortawesome/pro-regular-svg-icons';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';
import { InsightsActions as InsightsActionsAPI } from '../../api/lambda/InsightsActions';
import {
	LambdaDashboardSummaryRequest,
	LambdaGraphSummariesRequest,
	StatSummary,
} from '../../api/types/lambda-graph-summaries-request';
import { TDashboard } from '../../api/dashbboards/Dashboards';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import { LambdaGraphSummariesResponse } from '../../api/types/lambda-graph-summaries-response';
import ChatInterface, { ChatMessages, Message } from './ChatInterface';
import { Permission } from '../../enums/permission.enum';
import DiscoverQuestionsModal from './DiscoverQuestionsModal';
import { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

/** Dashboard Summary Card — collapsible card showing the AI-generated dashboard summary */
const DashboardSummaryCard: React.FC<{
	summaryText: string;
	collapsedMaxLines?: number;
}> = ({ summaryText, collapsedMaxLines = 4 }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const lines = summaryText.split(/[.!?]\s+/).filter((s) => s.trim());
	const shouldCollapse = lines.length > collapsedMaxLines;

	const displayText =
		shouldCollapse && !isExpanded
			? lines.slice(0, collapsedMaxLines).join('. ') + '.'
			: summaryText;

	return (
		<div className="dashboard-summary-card">
			<div className="dashboard-summary-card__label">Dashboard Overview</div>
			<div className="dashboard-summary-card__text">{displayText}</div>
			{shouldCollapse && (
				<button
					className="insights-read-more-btn"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded ? 'Show less' : 'Read more'}
				</button>
			)}
		</div>
	);
};

/** Chart Highlight Card — shows a chart's AI highlight with an Analyze button */
const ChartHighlightCard: React.FC<{
	widgetName: string;
	highlightText?: string;
	isLoading: boolean;
	isAnalyzed: boolean;
	isAnalyzing: boolean;
	onAnalyze: () => void;
	insightsData?: InsightsActionsResponse;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}> = ({
	widgetName,
	highlightText,
	isLoading,
	isAnalyzed,
	isAnalyzing,
	onAnalyze,
	insightsData,
	onMouseEnter,
	onMouseLeave,
}) => {
	return (
		<div
			className="chart-highlight-card"
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			<div className="chart-highlight-card__header">
				<div className="chart-highlight-card__name">{widgetName}</div>
			</div>
			{isLoading ? (
				<div className="chart-highlight-card__loading">
					<Skeleton />
					<span className="spacer-xxs"></span>
					<Skeleton width="80%" />
				</div>
			) : (
				<>
					{highlightText && (
						<div className="chart-highlight-card__text">{highlightText}</div>
					)}
					{!isAnalyzed && !isAnalyzing && (
						<Button
							variant="link"
							className="chart-highlight-card__analyze-btn"
							onClick={onAnalyze}
						>
							Analyze
						</Button>
					)}
					{isAnalyzing && (
						<div className="chart-highlight-card__analyzing">
							<Skeleton width="90%" />
							<span className="spacer-xxs"></span>
							<Skeleton width="70%" />
						</div>
					)}
					{isAnalyzed && insightsData && (
						<div className="chart-highlight-card__insights">
							<InsightsActions
								insights={insightsData}
								hasBackground={false}
								collapsible={true}
								collapsedMaxItems={3}
							/>
						</div>
					)}
				</>
			)}
		</div>
	);
};

interface InsightsPanelProps {
	widgets: Widget[];
	insightsActions?: { entityId: number; insightsResponse: InsightsActionsResponse }[];
	setInsightsActions: React.Dispatch<
		React.SetStateAction<{ entityId: number; insightsResponse: InsightsActionsResponse }[]>
	>;
	isInsightsOpen: boolean;
	setIsInsightsOpen: (open: boolean) => void;
	openDashboardInsightsPanel: (open: boolean) => void;
	insightsEntities: TDashboardInightsEntity[];
	dashboard: TDashboard;
	setLambdaGraphHighlights: React.Dispatch<React.SetStateAction<LambdaGraphSummariesResponse[]>>;
	lambdaGraphHighlights: LambdaGraphSummariesResponse[];
	setDashboardSummaryText: React.Dispatch<React.SetStateAction<string | undefined>>;
	dashboardSummaryText: string | undefined;
	beginInsightsForWidget: (widget: Widget) => void;
	onHighlightWidget?: (widget: Widget | undefined) => void;
}

const InsightsDashboardPanel: React.FC<InsightsPanelProps> = ({
	widgets,
	insightsActions,
	setInsightsActions,
	isInsightsOpen,
	setIsInsightsOpen,
	openDashboardInsightsPanel,
	insightsEntities,
	dashboard,
	setLambdaGraphHighlights,
	lambdaGraphHighlights,
	setDashboardSummaryText,
	dashboardSummaryText,
	beginInsightsForWidget,
	onHighlightWidget,
}) => {
	const [showDashboardSummaryInsights, setShowDashboardSummaryInsights] =
		useState<boolean>(false);
	const [isLoadingCalculationInsights, setIsLoadingCalculationInsights] =
		useState<boolean>(false);
	const [insightsFetchError, setInsightsFetchError] = useState(false);
	const [announcement, setAnnouncement] = useState('');
	const { currentGraphSummaryStats } = useCommonStoreContext();
	const { zoneSettings } = useApplication();
	const [prevStatsSummary, setPrevStatsSummary] = useState<StatSummary[]>([]);
	const isGranted = useIsGranted();
	const hasViewQuestionPerm = isGranted(Permission.ViewQuestion);
	const [isDiscoverQuestionsModalOpen, setIsDiscoverQuestionsModalOpen] = useState(false);
	const { dashboardId } = useParams();

	// Track which widgets are being analyzed or have been analyzed
	const [analyzingWidgetIds, setAnalyzingWidgetIds] = useState<Set<number>>(new Set());
	const [analyzedWidgetIds, setAnalyzedWidgetIds] = useState<Set<number>>(new Set());

	// Chat state for shared thread
	const [chatMessages, setChatMessages] = useState<Message[]>([]);
	const [isChatThinking, setIsChatThinking] = useState(false);
	const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const threadRef = useRef<HTMLDivElement | null>(null);
	const sidebarContainerRef = useRef<HTMLDivElement | null>(null);
	const isNearBottomRef = useRef(true);

	// Dynamic sidebar height: measure panel position and adjust to fill viewport
	useEffect(() => {
		const sidebarEl = sidebarContainerRef.current;
		if (!sidebarEl) return;

		const panelEl = sidebarEl.closest('.right-sidebar-panel') as HTMLElement;
		if (!panelEl) return;

		const scrollContainer = panelEl.closest('.pf-c-page__main') || document.documentElement;

		let rafId: number | null = null;

		const updateHeight = () => {
			const panelRect = panelEl.getBoundingClientRect();
			const footer = document.querySelector('.ribbon-footer');
			const footerTop = footer ? footer.getBoundingClientRect().top : window.innerHeight;

			const bottom = Math.min(window.innerHeight, footerTop);
			const newHeight = Math.floor(Math.max(bottom - panelRect.top, 200));

			const currentHeight = parseInt(panelEl.style.height || '0', 10);
			if (Math.abs(newHeight - currentHeight) > 1) {
				panelEl.style.height = `${newHeight}px`;
				panelEl.style.maxHeight = `${newHeight}px`;
			}
		};

		const onScrollOrResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				updateHeight();
				rafId = null;
			});
		};

		scrollContainer.addEventListener('scroll', onScrollOrResize, { passive: true });
		window.addEventListener('resize', onScrollOrResize, { passive: true });

		const subheader = document.querySelector('.sub-nav');
		let observer: ResizeObserver | null = null;
		if (subheader) {
			observer = new ResizeObserver(onScrollOrResize);
			observer.observe(subheader);
		}

		updateHeight();

		return () => {
			scrollContainer.removeEventListener('scroll', onScrollOrResize);
			window.removeEventListener('resize', onScrollOrResize);
			observer?.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
			panelEl.style.height = '';
			panelEl.style.maxHeight = '';
		};
	}, []);

	// Floating nav state
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);
	const [showJumpToOverview, setShowJumpToOverview] = useState(false);
	const overviewRef = useRef<HTMLDivElement | null>(null);

	// Build cumulative chat context
	const buildChatContext = (): string => {
		const parts: string[] = [];

		// Dashboard identity
		if (dashboard?.name) {
			parts.push(`Dashboard: ${dashboard.name}`);
		}

		// Dashboard summary
		if (dashboardSummaryText) {
			parts.push(`Dashboard Summary:\n${dashboardSummaryText}`);
		}

		// All chart highlights
		if (lambdaGraphHighlights?.length > 0) {
			const highlightParts = lambdaGraphHighlights.map((h) => {
				const widget = insightWidgets.find((w) => {
					const graphId = w.chart?.toString() || w.report?.toString();
					return graphId === h.graphId;
				});
				const name = widget ? getNameFromWidget(widget) : h.graphId;
				return `${name}: ${h.highlight}`;
			});
			parts.push(`Chart Highlights:\n${highlightParts.join('\n')}`);
		}

		// Stats for all charts
		if (currentGraphSummaryStats?.length > 0) {
			const statParts = currentGraphSummaryStats.map(
				(s) =>
					`${s.entityName}: mean=${s.mean}, total=${s.total}, min=${s.overallMin}, max=${s.overallMax}`
			);
			parts.push(`Graph Statistics:\n${statParts.join('\n')}`);
		}

		// Insights for all analyzed charts
		if (insightsActions && insightsActions.length > 0) {
			insightsActions.forEach(({ entityId, insightsResponse }) => {
				if (
					insightsResponse?.insights?.length > 0 ||
					insightsResponse?.actions?.length > 0
				) {
					const widget = insightWidgets.find(
						(w) => w.chart === entityId || w.report === entityId
					);
					const name = widget ? getNameFromWidget(widget) : `Entity ${entityId}`;
					if (insightsResponse.insights?.length > 0) {
						parts.push(
							`Insights for ${name}:\n- ${insightsResponse.insights.join('\n- ')}`
						);
					}
					if (insightsResponse.actions?.length > 0) {
						parts.push(
							`Actions for ${name}:\n- ${insightsResponse.actions.join('\n- ')}`
						);
					}
				}
			});
		}

		return parts.join('\n\n');
	};

	// Track when insights arrive for analyzed widgets
	useEffect(() => {
		if (!insightsActions) return;

		insightsActions.forEach(({ entityId, insightsResponse }) => {
			if (insightsResponse?.actions?.length > 0 || insightsResponse?.insights?.length > 0) {
				setAnalyzingWidgetIds((prev) => {
					const next = new Set(prev);
					next.delete(entityId);
					return next;
				});
				setAnalyzedWidgetIds((prev) => new Set([...prev, entityId]));
			}
		});
	}, [insightsActions]);

	// Fetch dashboard summary + graph highlights
	const fetchDashboardInsights = () => {
		if (currentGraphSummaryStats.length === 0) return;

		setShowDashboardSummaryInsights(false);
		setIsLoadingCalculationInsights(true);
		setInsightsFetchError(false);

		const request: LambdaGraphSummariesRequest = {
			dashboardId: dashboard.id?.toString() ?? '0',
			dashboardTitle: dashboard.name,
			filters: { range: 'last_28_days' },
			graphPayloads: currentGraphSummaryStats,
			metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
		};

		setPrevStatsSummary(currentGraphSummaryStats);

		retryWithBackoff(() => InsightsActionsAPI.GetGraphSummariesInsights(request), 2, 500)
			.then((response) => {
				setLambdaGraphHighlights(response.data);

				const dashboardSummaryRequest: LambdaDashboardSummaryRequest = {
					dashboardId: dashboard.id?.toString() ?? '0',
					dashboardTitle: dashboard.name,
					filters: { range: 'last_28_days' },
					highlights: response.data,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				};

				return retryWithBackoff(
					() => InsightsActionsAPI.GetDashboardSummaryInsights(dashboardSummaryRequest),
					3,
					1000
				);
			})
			.then((response) => {
				setDashboardSummaryText(response.data.dashboardSummary);
				setShowDashboardSummaryInsights(true);
			})
			.catch((error: AxiosError) => {
				console.error('Insights fetch error after retries:', {
					message: error.message,
					code: error.code,
					status: error.response?.status,
					data: error.response?.data,
					isNetworkError: !error.response,
				});
				setInsightsFetchError(true);
				setShowDashboardSummaryInsights(true);
			})
			.finally(() => {
				setIsLoadingCalculationInsights(false);
			});
	};

	// Auto-fetch on stats change
	useEffect(() => {
		if (currentGraphSummaryStats.length === 0) return;

		if (
			(isInsightsOpen && dashboardSummaryText) ||
			(prevStatsSummary.length > 0 && prevStatsSummary == currentGraphSummaryStats) ||
			dashboardSummaryText
		) {
			setShowDashboardSummaryInsights(true);
			setIsLoadingCalculationInsights(false);
			return;
		}

		fetchDashboardInsights();
	}, [currentGraphSummaryStats]);

	useEffect(() => {
		if (isInsightsOpen && dashboardSummaryText) {
			setShowDashboardSummaryInsights(true);
		}
		if (isInsightsOpen && lambdaGraphHighlights && lambdaGraphHighlights.length > 0) {
			setIsLoadingCalculationInsights(false);
		}
	}, [isInsightsOpen, dashboardSummaryText, lambdaGraphHighlights]);

	useEffect(() => {
		if (!isLoadingCalculationInsights && showDashboardSummaryInsights) {
			// Clear the live region first
			setAnnouncement('');
			// Delay slightly so the screen reader detects it as a change
			setTimeout(() => {
				setAnnouncement('Dashboard insight summary and graph summaries have loaded.');
			}, 50);
		}
	}, [isLoadingCalculationInsights, showDashboardSummaryInsights]);

	// Scroll tracking for floating nav
	useEffect(() => {
		const container = threadRef.current;
		if (!container) return;

		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
			const isNearTop = scrollTop < 100;

			isNearBottomRef.current = isNearBottom;

			setShowJumpToLatest(!isNearBottom && chatMessages.length > 0);
			setShowJumpToOverview(!isNearTop && scrollTop > 200);
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, [chatMessages.length]);

	// Auto-scroll when near bottom or when the user just sent a message (isChatThinking)
	useEffect(() => {
		if (chatMessages.length > 0 || isChatThinking) {
			if (isNearBottomRef.current || isChatThinking) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						if (threadRef.current) {
							threadRef.current.scrollTop = threadRef.current.scrollHeight;
						}
					});
				});
			}
		}
	}, [chatMessages, isChatThinking]);

	const scrollToBottom = () => {
		threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
	};

	const scrollToOverview = () => {
		overviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	const getNameFromWidget = (widget: Widget) => {
		if (widget.widget_type == 'chart') {
			return insightsEntities.find((x) => x.chart?.id == widget.chart)?.chart?.name ?? '';
		}
		if (widget.widget_type == 'pivot_table') {
			return (
				insightsEntities.find(
					(x) => x.report?.id == widget.report || x.table?.id == widget.report
				)?.report?.name ?? ''
			);
		}
		return '';
	};

	const getHighlightForWidget = (widget: Widget): string | undefined => {
		if (!lambdaGraphHighlights || lambdaGraphHighlights.length === 0) return undefined;
		const graphId = widget.chart ? widget.chart.toString() : widget.report?.toString();
		return lambdaGraphHighlights.find((x) => x.graphId == graphId)?.highlight;
	};

	const getInsightsForWidget = (widget: Widget): InsightsActionsResponse | undefined => {
		if (!insightsActions) return undefined;
		const entityId = widget.chart ?? widget.report ?? 0;
		const found = insightsActions.find((x) => x.entityId === entityId);
		return found?.insightsResponse;
	};

	const handleAnalyze = (widget: Widget) => {
		const entityId = widget.chart ?? widget.report ?? 0;
		setAnalyzingWidgetIds((prev) => new Set([...prev, entityId]));
		beginInsightsForWidget(widget);
	};

	const insightWidgets = widgets.filter(
		(x) => x.widget_type === 'chart' || x.widget_type == 'pivot_table'
	);

	const closeSidebar = () => {
		setIsInsightsOpen(false);
		openDashboardInsightsPanel(false);
		setInsightsActions([]);
		setAnalyzedWidgetIds(new Set());
		setAnalyzingWidgetIds(new Set());
		setChatMessages([]);
	};

	const chatContext = useMemo(
		() => buildChatContext(),
		[
			dashboard?.name,
			dashboardSummaryText,
			lambdaGraphHighlights,
			currentGraphSummaryStats,
			insightsActions,
		]
	);

	return (
		<div
			className="insights-sidebar-redesign"
			ref={sidebarContainerRef}
			role="complementary"
			aria-label="AI Insights"
		>
			{/* Sticky Header */}
			<div className="insights-sticky-header">
				<div className="insights-sticky-header__left">
					<FontAwesomeIcon
						icon={faSparkles}
						color="#0073c3"
					/>
					<span className="insights-sticky-header__title">AI * Insights</span>
				</div>
				<div className="insights-sticky-header__right">
					{hasViewQuestionPerm && (
						<Tooltip content="Discover Questions">
							<Button
								variant="plain"
								className="insights-header-btn"
								onClick={() => setIsDiscoverQuestionsModalOpen(true)}
							>
								<FontAwesomeIcon icon={faCompass} />
							</Button>
						</Tooltip>
					)}
					<Tooltip content="Expand">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={() => setIsInsightsModalOpen(true)}
						>
							<FontAwesomeIcon icon={faExpand} />
						</Button>
					</Tooltip>
					<Tooltip content="Close">
						<Button
							variant="plain"
							className="insights-header-btn"
							onClick={closeSidebar}
						>
							<FontAwesomeIcon icon={faTimes} />
						</Button>
					</Tooltip>
				</div>
			</div>

			{/* Scrollable Thread */}
			<div
				className="insights-thread"
				ref={threadRef}
				tabIndex={0}
			>
				{/* Dashboard Summary */}
				<div ref={overviewRef}>
					{!showDashboardSummaryInsights && (
						<div className="insights-loading">
							<Skeleton />
							<span className="spacer-xxs"></span>
							<Skeleton />
							<span className="spacer-xxs"></span>
							<Skeleton width="60%" />
						</div>
					)}
					{showDashboardSummaryInsights && dashboardSummaryText && (
						<DashboardSummaryCard summaryText={dashboardSummaryText} />
					)}
					{showDashboardSummaryInsights &&
						!dashboardSummaryText &&
						insightsFetchError && (
							<div style={{ padding: '1rem', textAlign: 'center' }}>
								<p style={{ color: '#6a6e73', marginBottom: '0.5rem' }}>
									Failed to generate insights.
								</p>
								<Button
									variant="link"
									onClick={fetchDashboardInsights}
								>
									Try again
								</Button>
							</div>
						)}
				</div>

				{/* Chart Highlight Cards */}
				{insightWidgets.map((widget) => {
					const entityId = widget.chart ?? widget.report ?? 0;
					const widgetName = getNameFromWidget(widget);
					const highlightText = getHighlightForWidget(widget);
					const isAnalyzing = analyzingWidgetIds.has(entityId);
					const isAnalyzed = analyzedWidgetIds.has(entityId);
					const widgetInsights = getInsightsForWidget(widget);

					return (
						<ChartHighlightCard
							key={widget.id}
							widgetName={widgetName}
							highlightText={highlightText}
							isLoading={isLoadingCalculationInsights}
							isAnalyzed={isAnalyzed}
							isAnalyzing={isAnalyzing}
							onAnalyze={() => handleAnalyze(widget)}
							insightsData={widgetInsights}
							onMouseEnter={() => onHighlightWidget?.(widget)}
							onMouseLeave={() => onHighlightWidget?.(undefined)}
						/>
					);
				})}

				{/* Chat messages in shared thread */}
				{chatMessages.length > 0 && (
					<div className="insights-chat-thread">
						<ChatMessages
							messages={chatMessages}
							isThinking={isChatThinking}
						/>
					</div>
				)}

				{/* Floating nav buttons */}
				{showJumpToOverview && (
					<button
						className="floating-nav-btn floating-nav-btn--top"
						onClick={scrollToOverview}
					>
						<FontAwesomeIcon icon={faChevronUp} /> Overview
					</button>
				)}
				{showJumpToLatest && (
					<button
						className="floating-nav-btn floating-nav-btn--bottom"
						onClick={scrollToBottom}
					>
						<FontAwesomeIcon icon={faChevronDown} /> Latest
					</button>
				)}
			</div>

			{/* Hidden live region for announcements */}
			<div
				aria-live="polite"
				style={{
					position: 'absolute',
					left: '-9999px',
					height: '1px',
					width: '1px',
					overflow: 'hidden',
				}}
			>
				{announcement}
			</div>

			{/* Pinned Chat Input */}
			<div className="insights-pinned-input">
				{chatMessages.length === 0 && !hasInteracted && (
					<small className="insights-chat-prompt">Ask a follow-up question</small>
				)}
				<ChatInterface
					context={chatContext}
					contextId={dashboard.id}
					externalThread={true}
					onMessagesChange={setChatMessages}
					onThinkingChange={setIsChatThinking}
					showModal={isInsightsModalOpen}
					onModalClose={() => setIsInsightsModalOpen(false)}
					disabled={!showDashboardSummaryInsights}
					onFirstInteraction={() => setHasInteracted(true)}
					modalHeaderContent={
						dashboardSummaryText ? (
							<DashboardSummaryCard summaryText={dashboardSummaryText} />
						) : undefined
					}
				/>
			</div>

			<DiscoverQuestionsModal
				isOpen={isDiscoverQuestionsModalOpen}
				onClose={() => setIsDiscoverQuestionsModalOpen(false)}
				dashboardId={dashboardId ? parseInt(dashboardId) : undefined}
			/>
		</div>
	);
};

export default InsightsDashboardPanel;
